import { useState } from 'react'
import { Icon } from '@iconify/react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '@/api/client'
import { useStorefrontContent } from '@/branding/BrandingProvider'
import { Seo } from '@/seo/Seo'

interface CaregiverFormData {
  roleLabel: string
  firstName: string
  lastName: string
  phone: string
  institution: string
  email: string
  password: string
}

export function SignUpCaregiverPage() {
  const { auth } = useStorefrontContent()
  const roleTitle = auth?.accountType?.roles?.caregiver?.title || 'Caregiver'
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState<CaregiverFormData>({
    roleLabel: '',
    firstName: '',
    lastName: '',
    phone: '',
    institution: '',
    email: '',
    password: '',
  })

  const updateField = (field: keyof CaregiverFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      // See SignUpPatientPage: trim the email (autocomplete appends a space) and keep
      // the password out of /registration, which ignores it.
      const { email, password, ...profile } = form
      // The response now reports whether the OTP email actually got out. `false` means
      // the account IS ready and the code IS stored — only delivery failed — so the
      // verify screen offers Resend rather than implying the sign-up itself failed.
      const signUp = await api.post<{ emailSent?: boolean }>('/registration', {
        ...profile,
        email: email.trim(),
        roleLabel: form.roleLabel || 'Caregiver',
      })
      navigate('/verify-otp', { state: { email: email.trim(), password, emailSent: signUp?.emailSent } })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign up')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6 py-12">
      <Seo
        title="Caregiver Sign Up — Zenzers Real-Time Care Alerts"
        description="Create a Zenzers caregiver account to stay connected with real-time alerts, reports, and remote monitoring for the people you care for."
        path="/sign-up-caregiver"
      />
      <form onSubmit={handleSubmit} className="w-full max-w-lg bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
        <Link
          to="/account-type"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-navy mb-6 transition-colors"
        >
          <Icon icon="solar:arrow-left-linear" /> Back
        </Link>

        <h1 className="text-2xl font-medium tracking-tight text-navy mb-1">{roleTitle} Registration</h1>
        <p className="text-sm text-slate-500 font-light mb-6">Create your {roleTitle.toLowerCase()} account.</p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
          <select
            required
            value={form.roleLabel}
            onChange={(e) => updateField('roleLabel', e.target.value)}
            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none bg-white"
          >
            <option value="">Select role...</option>
            <option value="Caregiver">Caregiver</option>
            <option value="Family Member">Family Member</option>
            <option value="Care Aide">Care Aide</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">First Name</label>
            <input
              type="text"
              required
              value={form.firstName}
              onChange={(e) => updateField('firstName', e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Last Name</label>
            <input
              type="text"
              required
              value={form.lastName}
              onChange={(e) => updateField('lastName', e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none"
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
          <input
            type="tel"
            required
            value={form.phone}
            onChange={(e) => updateField('phone', e.target.value)}
            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">Institution (optional)</label>
          <input
            type="text"
            value={form.institution}
            onChange={(e) => updateField('institution', e.target.value)}
            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => updateField('email', e.target.value)}
            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none"
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
          <input
            type="password"
            required
            minLength={8}
            value={form.password}
            onChange={(e) => updateField('password', e.target.value)}
            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-navy text-white py-3 rounded-lg font-medium text-sm hover:bg-slate-800 transition-colors disabled:opacity-50"
        >
          {loading ? 'Creating Account...' : 'Create Account'}
        </button>
      </form>
    </div>
  )
}
